import { z } from 'zod';
import { CORE_ROUTING_POLICY_IDS } from '../../../core/connectors/routing';
import { connectorCommonProps, type ConnectorManifest, type ConnectorPack } from '../../../lib/diagram/connector-manifest.types';
import { registerConnector, registerRelationshipPack } from '../../../lib/diagram/registries';
import { BpmnRelationshipPack } from './bpmnRelationships.pack';


const bpmnConnectorProps = connectorCommonProps.extend({
  routingType: z.enum(['straight', 'orthogonal', 'orthogonalRounded', 'bezier']).default('orthogonal'),
  label: z.string().optional(),
  fromAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  toAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  fromOffset: z.number().min(0).max(1).optional(),
  toOffset: z.number().min(0).max(1).optional(),
});

type BpmnConnectorProps = z.infer<typeof bpmnConnectorProps>;

export const MessageFlowConnectorManifest: ConnectorManifest<BpmnConnectorProps> = {
  id: 'message_flow',
  version: '1.0.0',
  category: 'flowchart',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.processFlow,
  label: 'Message Flow',
  iconId: 'message-flow',
  propsSchema: bpmnConnectorProps,
  defaultProps: () => ({
    color: '#334155',
    width: 1.5,
    dashed: true,
    dash: [8, 4],
    endArrow: { type: 'circle', size: 6 },
    startArrow: { type: 'none', size: 6 },
    routingType: 'orthogonal',
  }),
  ui: {
    group: 'routing',
    collapsedByDefault: false,
  },
};

export const ExceptionFlowConnectorManifest: ConnectorManifest<BpmnConnectorProps> = {
  id: 'exception_flow',
  version: '1.0.0',
  category: 'flowchart',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.processFlow,
  label: 'Exception Flow',
  iconId: 'exception-flow',
  propsSchema: bpmnConnectorProps,
  defaultProps: () => ({
    color: '#dc2626',
    width: 2,
    dashed: false,
    endArrow: { type: 'triangle', size: 8 },
    startArrow: { type: 'none', size: 6 },
    routingType: 'orthogonal',
  }),
  ui: {
    group: 'routing',
    collapsedByDefault: false,
  },
};

export const DataAssociationConnectorManifest: ConnectorManifest<BpmnConnectorProps> = {
  id: 'data_association',
  version: '1.0.0',
  category: 'flowchart',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.processFlow,
  label: 'Data Association',
  iconId: 'data-association',
  propsSchema: bpmnConnectorProps,
  defaultProps: () => ({
    color: '#475569',
    width: 1.5,
    dashed: true,
    dash: [4, 4],
    endArrow: { type: 'chevron', size: 7 },
    startArrow: { type: 'none', size: 6 },
    routingType: 'orthogonal',
  }),
  ui: {
    group: 'routing',
    collapsedByDefault: false,
  },
};

export const BpmnConnectorPack: ConnectorPack = {
  meta: {
    id: 'bpmn-connectors',
    name: 'BPMN Connectors',
    version: '1.0.0',
  },
  diagramType: 'flowchart',
  connectors: [
    MessageFlowConnectorManifest as ConnectorManifest<unknown>,
    ExceptionFlowConnectorManifest as ConnectorManifest<unknown>,
    DataAssociationConnectorManifest as ConnectorManifest<unknown>,
  ],
};

export function registerBpmnConnectors(): void {
  registerRelationshipPack(BpmnRelationshipPack);
  registerConnector('flowchart', MessageFlowConnectorManifest);
  registerConnector('flowchart', ExceptionFlowConnectorManifest);
  registerConnector('flowchart', DataAssociationConnectorManifest);
}
